import React, {useEffect, useRef, useState} from 'react'
import {Formik, Form, Field, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import moment from 'moment'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import {Modal} from 'react-bootstrap'
import {FaTimesCircle} from 'react-icons/fa'
import Astericsign from './Astericsign'
import {
  addressValidation,
  emailOptionalValidation,
  firstNameValidation,
  lastNameValidation,
  phoneNumberValidation,
} from './helper/ValidationHelperComponent'
import PrimaryButton from '../common/buttons/PrimaryButton'
import ImagePickerUploader from '../ImagePickerUploader'
import {useReduxAuth} from '../../../auth'
import {canAddPatients} from '../../helper/userHelpers'
import RequiredSubscriptionModal from '../modals/RequiredSubscriptionModal'
import ConsentFormUploader from '../ConsentFormUploader'

interface FormValues {
  first_name: string
  last_name: string
  email: string
  phone_number: string
  address: string
  dob: string | null
  gender: string
  profile_image?: string
  consent_form_url?: string | null
}

interface PatientFormProps {
  isOpen: boolean
  onClose: () => void
  isLoading?: boolean
  handleSubmit: (values: any, resetForm: () => void) => void
  initialData?: any
  mode: 'add' | 'edit'
}

const validationSchemas = Yup.object({
  first_name: firstNameValidation,
  last_name: lastNameValidation,
  email: emailOptionalValidation,
  phone_number: phoneNumberValidation,
  address: addressValidation,

  dob: Yup.string()
    .required('Date of Birth is required')
    .nullable()
    .test('is-valid', 'Invalid date', (value) =>
      value ? moment(value, 'YYYY-MM-DD', true).isValid() : true
    )
    .test('not-in-future', 'Future date not allowed', (value) =>
      value ? moment(value).isSameOrBefore(moment()) : true
    )
    .test('valid-age', 'Age must be at least 3 years', (value) =>
      value ? moment().diff(moment(value, 'YYYY-MM-DD'), 'years') >= 3 : true
    ),
  gender: Yup.string().required('Gender is required'),
  profile_image: Yup.string().nullable(),
})

// Helper function to normalize phone number to E.164 format
const normalizePhoneToE164 = (phone: string | null | undefined): string => {
  if (!phone) return ''
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '')
  // If it already starts with +, return as is
  if (cleaned.startsWith('+')) return cleaned
  // If it's all digits and not empty, add + prefix
  if (cleaned.length > 0) return `+${cleaned}`
  return ''
}

function PatientForm({isOpen, onClose, isLoading, handleSubmit, initialData,mode}: PatientFormProps) {
  const {currentUser} = useReduxAuth()
  const resetFormRef = useRef<(() => void) | null>(null)
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(initialData?.profile_image)

  useEffect(() => {
    if (initialData?.profile_image) {
      setImageUrl(initialData.profile_image)
    }
  }, [initialData?.profile_image])

  const initialValues: FormValues & { consent_form?: File | null } = {
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    email: initialData?.email || '',
    phone_number: normalizePhoneToE164(initialData?.phone_number),
    address: initialData?.address || '',
    dob: initialData?.dob ? moment.utc(initialData.dob).format('YYYY-MM-DD') : null,
    gender: initialData?.gender || '',
    profile_image: initialData?.profile_image || null,
     consent_form: null,
     consent_form_url: initialData?.consent_form_url || null,
  }

  const handleClose = () => {
     if (mode === 'edit' && formDirty) {
      setShowDiscardModal(true)
    } else {
      onClose()
    }
  }

  const discardChanges = () => {
    resetFormRef.current?.()
    setFormDirty(false)
    setShowDiscardModal(false)
    onClose()
  }

  // reset form when user close and re-open the form
  useEffect(() => {
    if (isOpen && resetFormRef.current) {
      resetFormRef.current()
      setFormDirty(false)
    }
  }, [isOpen])
  
  const handleSupportClick = () => {
    const subject = `Portal Support Request from ${encodeURIComponent(currentUser?.name || '')}`
    window.location.href = `mailto:support@candihealth.com?subject=${subject}`
  }
  
  return (
    <>
      {isOpen && (
        <div className='modal-backdrop show' style={{backgroundColor: 'rgba(0,0,0,0.5)'}} />
      )}

      {canAddPatients(currentUser) ? (
        <>
          <div className={`modal fade ${isOpen ? 'show d-block' : ''}`} tabIndex={-1}>
            <div className='modal-dialog modal-dialog-centered modal-lg'>
              <div className='modal-content rounded-4 shadow'>
                <div className='modal-header bg-light border-bottom-0 mb-0'>
                  <h5 className='modal-title fw-semibold'>Patient Form</h5>
                  <button type='button' className='btn-close' onClick={handleClose}></button>
                </div>

                <div className='modal-body p-4'>
                  <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validationSchema={validationSchemas}
                    onSubmit={({...values}, {setSubmitting, resetForm}) => {
                      // const trimmedValues = {
                      //   ...values,
                      //   address: values.address?.replace(/\s+/g, ' ').trim() || '',
                      // }

                      const rawPhone = values.phone_number?.replace(/[^\d+]/g, '') || ''
                      const trimmedValues = {
                        ...values,

                        // force lowercase + trim for email
                        email: values.email?.toLowerCase().trim() || '',
                        // replace the formatted phone with raw digits

                        phone_number: rawPhone,

                        address: values.address?.replace(/\s+/g, ' ').trim() || '',
                      }

                      handleSubmit(trimmedValues, resetForm)

                      // resetForm({
                      //   values: {
                      //     first_name: '',
                      //     last_name: '',
                      //     dob: '', //to make blank this field we added this reset form
                      //     phone_number: '',
                      //     email: '',
                      //     address: '',
                      //     gender: '',
                      //     profile_image: '',
                      //   },
                      // })
                      setFormDirty(false)
                      // onClose()
                    }}
                  >
                    {({isSubmitting, values, setFieldValue, resetForm}) => {
                      resetFormRef.current = resetForm

                      return (
                        <Form onChange={() => setFormDirty(true)}>
                          <>
                            <div className='mb-3'>
                              <div className='mb-4'>
                                <ImagePickerUploader
                                  imageUrl={imageUrl}
                                  onChange={(url) => {
                                    setImageUrl(url) // for preview
                                    setFieldValue('profile_image', url) // for Formik to submit
                                  }}
                                />
                              </div>

                              <div className='d-flex gap-3 mb-3'>
                                <div className='flex-grow-1'>
                                  <label className='form-label'>
                                    First Name <Astericsign />
                                  </label>
                                  <Field
                                    name='first_name'
                                    className='form-control'
                                    onInput={(e: any) => {
                                      e.currentTarget.value = e.currentTarget.value.replace(
                                        /[^a-zA-Z\s]/g,
                                        ''
                                      )
                                    }}
                                  />
                                  <ErrorMessage
                                    name='first_name'
                                    component='div'
                                    className='text-danger'
                                  />
                                </div>

                                <div className='flex-grow-1'>
                                  <label className='form-label'>
                                    Last Name <Astericsign />{' '}
                                  </label>
                                  <Field
                                    name='last_name'
                                    className='form-control'
                                    onInput={(e: any) => {
                                      e.currentTarget.value = e.currentTarget.value.replace(
                                        /[^a-zA-Z\s]/g,
                                        ''
                                      )
                                    }}
                                  />
                                  <ErrorMessage
                                    name='last_name'
                                    component='div'
                                    className='text-danger'
                                  />
                                </div>
                              </div>
                              <div className='mb-3'>
                                <div className='d-flex gap-3 mb-3'>
                                  <div className='flex-grow-1'>
                                    <label className='form-label'>
                                      Email
                                      {/* <Astericsign /> */}
                                    </label>
                                    <Field name='email' type='email' className='form-control' />
                                    <ErrorMessage
                                      name='email'
                                      component='div'
                                      className='text-danger'
                                    />
                                  </div>
                                  <div className='flex-grow-1'>
                                    <label className='form-label'>
                                      Phone <Astericsign />
                                    </label>
                                    <PhoneInput
                                      defaultCountry='US'
                                      international
                                      value={values.phone_number || ''}
                                      onChange={(value) =>{
                                        setFieldValue('phone_number', value || '')
                                        setFormDirty(true)
                                      }}
                                      maxLength={15}
                                      inputMode='tel'
                                      className='form-control'
                                    />
                                    <ErrorMessage
                                      name='phone_number'
                                      component='div'
                                      className='text-danger'
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className='mb-3'>
                              <label className='form-label'>Address</label>
                              <Field name='address' as='textarea' className='form-control' />
                              <ErrorMessage
                                name='address'
                                component='div'
                                className='text-danger'
                              />
                            </div>

                            <div className='mb-3'>
                              <div className='d-flex gap-5 mb-3'>
                                <div className='flex-grow-1'>
                                  <label className='form-label'>Date of Birth</label> <Astericsign />
                                  <Field name='dob'>
                                    {({field, form}: any) => (
                                      <input
                                        {...field}
                                        type='date'
                                        className='form-control'
                                        max={moment().format('YYYY-MM-DD')}
                                        onChange={(e: any) => {
                                          let value = e.target.value
                                          if (value) {
                                            const parts = value.split('-')
                                            if (parts[0] && parts[0].length > 4) {
                                              const validYear = parts[0].substring(0, 4)
                                              const validDate = parts[1] && parts[2] 
                                                ? `${validYear}-${parts[1]}-${parts[2]}`
                                                : validYear
                                              form.setFieldValue('dob', validDate)
                                              setFormDirty(true)
                                            } else {
                                              form.setFieldValue('dob', value)
                                              setFormDirty(true)
                                            }
                                          } else {
                                            form.setFieldValue('dob', value)
                                            setFormDirty(true)
                                          }
                                        }}
                                      />
                                    )}
                                  </Field>
                                  <ErrorMessage
                                    name='dob'
                                    component='div'
                                    className='text-danger'
                                  />
                                </div>

                                <div className='flex-grow-1'>
                                  <label className='form-label d-block'>
                                    Gender <Astericsign />
                                  </label>
                                  <div className='form-check form-check-inline'>
                                    <Field
                                      type='radio'
                                      name='gender'
                                      value='male'
                                      className='form-check-input text-black'
                                      id='male'
                                    />
                                    <label className='form-check-label text-black' htmlFor='male'>
                                      Male
                                    </label>
                                  </div>
                                  <div className='form-check form-check-inline'>
                                    <Field
                                      type='radio'
                                      name='gender'
                                      value='female'
                                      className='form-check-input text-black'
                                      id='female'
                                    />
                                    <label className='form-check-label text-black' htmlFor='female'>
                                      Female
                                    </label>
                                  </div>
                                  <ErrorMessage
                                    name='gender'
                                    component='div'
                                    className='text-danger'
                                  />
                                </div>
                              </div>
                            </div>

                            <div className='mt-4'>
                              <ConsentFormUploader
                                fileUrl={values.consent_form_url}
                                onChange={(file: File | null) => {
                                  if (file) {
                                      setFieldValue('consent_form', file)  
                                      setFieldValue('consent_form_url', null)  
                                    } else {
                                      setFieldValue('consent_form', null)
                                      setFieldValue('consent_form_url', initialData?.consent_form_url || null) 
                                    }
                                    setFormDirty(true)
                                  }}
                                showDownload={false}
                              />
                            </div>

                          </>
                          <div className='d-flex justify-content-between mt-5'>
                            <p className='fs-6 py-2'>
                            Want more patient info in system? Let us know by {' '}
                                      <span className='text-primary cursor-pointer'
                                        title='Contact support via email'
                                        onClick={handleSupportClick}
                                      >
                                        email
                                      </span>
                                    </p>
                            <PrimaryButton type='submit' className='' disabled={isLoading || isSubmitting}>
                              {(isLoading || isSubmitting) ? (
                                <span className='d-flex align-items-center'>
                                  <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>
                                  {mode === 'add' ? 'Adding...' : 'Updating...'}
                                </span>
                              ) : (
                                mode === 'add' ? 'Add' : 'Update'
                              )}
                            </PrimaryButton>
                          </div>
                        </Form>
                      )
                    }}
                  </Formik>
                </div>
              </div>
            </div>
          </div>

           {/* Confirm Discard Modal */}
          <Modal show={showDiscardModal} onHide={() => setShowDiscardModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                <FaTimesCircle className='text-danger me-2' />
                Unsaved Changes
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              You have entered information that hasn’t been saved yet. Are you sure you want to
              discard your changes?
            </Modal.Body>
            <Modal.Footer>
              <PrimaryButton color='secondary' onClick={() => setShowDiscardModal(false)}>
                Keep Editing
              </PrimaryButton>
              <PrimaryButton color='danger' onClick={discardChanges}>
                Discard Changes
              </PrimaryButton>
            </Modal.Footer>
          </Modal>
        </>
      ) : (
        <RequiredSubscriptionModal isOpen={isOpen} onClose={onClose} />
      )}
    </>
  )
}

export default PatientForm
